import { Component, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ContentfulService } from '../services/contentful.service';
import { EnvironmentVariablesService } from '../services/environment-variables.service';
import { SearchEngineService } from '../services/search-engine.service';
import { ContentfulConfigService } from '../services/contentful-config.service';
import { InvestorPortfolioSeriesReportingDealBreadcrumb } from '../models/classes/investor-portfolio-series-reporting-deal-breadcrumb.class';
import { BreadcrumbDataService } from '../services/breadcrumb-data.service';
import { KeyValuePair } from '../models/classes/key-value-pair.class';
import { HighchartsService } from '../services/highcharts-service.service';
import { DealDashboard } from '../models/classes/deal-dashboard.class';
import { ApiService } from '../core/http/api.service';
import { Asset } from '../models/classes/asset.class';
import { DealDashboardDetails } from '../models/classes/deal-dashboard-details.class';
import { SnapshotService } from '../services/snapshot.service';
import { UserService } from '../services/user.service';
import { LoggingService } from '../services/logging.service';
import { DealMeta } from '../models/classes/deal-meta.class';
import { NgTourService } from '../services/ng-tour-ui.service';
import { NgTourStepsService } from '../services/ng-tour-steps.service';
import { AssetClassPipe } from '../pipes/asset-class.pipe';
import { AssetSummaryContentful } from '../models/classes/asset-summary-contentful.class';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { TableLogicService } from '../services/table-logic.service';
import { ActivityTrackingService } from '../services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { NoAccessErrorComponent } from '../shared/no-access-error/no-access-error.component';
import { FormsModule } from '@angular/forms';
import { DataUnavailableCardComponent } from '../shared/data-unavailable-card/data-unavailable-card.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DealDashboardSummaryComponent } from './deal-dashboard-summary/deal-dashboard-summary.component';
import { SectorsWidgetComponent } from './sectors-widget/sectors-widget.component';
import { GeographyWidgetComponent } from './geography-widget/geography-widget.component';
import { LargestPositionWidgetComponent } from './largest-position-widget/largest-position-widget.component';
import { AssetsDetailTableComponent } from './assets-detail-table/assets-detail-table.component';
import { AssetSummaryComponent } from '../asset-dashboard/asset-summary/asset-summary.component';
import { DateFormatPipe } from '../pipes/date-format.pipe';
import { MatIconModule } from '@angular/material/icon';
import { AutoAdjustingDropdownComponent } from '../auto-adjusting-dropdown/auto-adjusting-dropdown.component';
import { GcmLoadingService, GcmDatePickerComponent } from '@gcm/gcm-ui-angular';
import { CaseStudyComponent } from './case-study/case-study.component';
import { CoInvestmentDashboard } from '../models/classes/co-investment-dashboard.class';
import { ErrorMessageService } from '../services/error-message.service';
import { Breadcrumb, BreadcrumbParentData } from '../models/interfaces/breadcrumb.interface';
import { AssetSummaryResponse, LandingPageResponse } from '../models/interfaces/contentful-response.interface';
import { parent } from '../models/classes/metadata.class';

@Component({
  selector: 'app-deal-dashboard',
  imports: [
    CommonModule,
    NoAccessErrorComponent,
    FormsModule,
    DataUnavailableCardComponent,
    MatTooltipModule,
    DealDashboardSummaryComponent,
    SectorsWidgetComponent,
    GeographyWidgetComponent,
    LargestPositionWidgetComponent,
    AssetsDetailTableComponent,
    RouterModule,
    AssetSummaryComponent,
    DateFormatPipe,
    MatIconModule,
    AutoAdjustingDropdownComponent,
    GcmDatePickerComponent,
    TourMatMenu,
    CaseStudyComponent
  ],
  templateUrl: './deal-dashboard.component.html',
  styleUrls: ['./deal-dashboard.component.scss']
})

export class DealDashboardComponent implements OnInit {
  private router = inject(Router);
  private contentfulService = inject(ContentfulService);
  private evService = inject(EnvironmentVariablesService);
  private seoService = inject(SearchEngineService);
  private contentfulConfig = inject(ContentfulConfigService);
  breadcrumbDataService = inject(BreadcrumbDataService);
  private activatedRoute = inject(ActivatedRoute);
  private highchartsService = inject(HighchartsService);
  private apiService = inject(ApiService);
  snapService = inject(SnapshotService);
  private userService = inject(UserService);
  private loadingService = inject(GcmLoadingService);
  private loggingService = inject(LoggingService);
  private ngTourSteps = inject(NgTourStepsService);
  private ngTourUi = inject(NgTourService);
  private assetClassPipe = inject(AssetClassPipe);
  private tableLogicService = inject(TableLogicService);
  activityTracking = inject(ActivityTrackingService);
  private dateFormat = inject(DateFormatPipe);
  private errorMessageService = inject(ErrorMessageService);

  errorMessage!: string;
  hasError = false;
  dealSummary!: string;
  dealTitle!: string;
  readonly iconSet = toSignal(
    this.contentfulService.getIconSetValue().pipe(filter((v): v is Record<string, string> => !!v)),
    { initialValue: {} as Record<string, string> }
  );
  private hasInitialized = false;
  private readonly initOnIconSet = effect(() => {
    if (Object.keys(this.iconSet()).length > 0 && !this.hasInitialized) {
      this.hasInitialized = true;
      this.getDealsCommonAssets();
      this.checkIfUserIsExternal();
    }
  });
  managerIds: number[] = [];
  asOfDropdownData: string[] = [];

  investorId = input<number>();
  seriesId = input<number>();
  dealId = input<number>();
  readonly reportingdate = input<string>();

  reportingPeriod = signal<string>('');
  selectedAsOfDate = computed<string>(() => {
    const period = this.reportingPeriod();
    return period ? this.dateFormat.transform(period, "MM/dd/yyyy") ?? '' : '';
  });

  private readonly syncReportingFromDate = effect(() => {
    const value = this.reportingdate();
    if (value) {
      this.setReportingDate(value);
    }
  });

  private setReportingDate(value: string): void {
    if (!value) return;
    const reportingPeriod = this.dateFormat.transform(value, "yyyy-MM-dd");

    if (reportingPeriod) {
      this.reportingPeriod.set(reportingPeriod);
    }
  }

  breadcrumbs: Breadcrumb[] = [];
  dealsForBreadcrumb = signal<InvestorPortfolioSeriesReportingDealBreadcrumb[]>([]);
  dealName = signal<string>('');
  dealNameForBreadcrumb = signal<string>('');
  seriesName = signal<string>('');
  portfolioName = signal<string>('');
  widgets: Record<string, KeyValuePair<string | number, number>[]> = {};
  otherCountryNames: string[] = [];
  error = this.highchartsService.noResultsText;
  dashboardData: DealDashboard = new DealDashboard();
  assets: Asset[] = [];
  summary: DealDashboardDetails = new DealDashboardDetails();
  firstTimeLoadingDeals = true;
  showNoAccessError = false;
  currency = 'USD';
  assetClassImage: string[] = [];
  isSingleAssetDeal = false;
  assetSummary = signal<AssetSummaryContentful>(new AssetSummaryContentful());
  dealDetailLabel = '';
  assetDetailLabel = '';
  isDealDataUnavailable = false;
  isDashboardApiError = false;
  assetMasterId!: number;
  showCaseStudy = false;
  coInvestmentCaseStudyDetails: CoInvestmentDashboard = new CoInvestmentDashboard();
  parentData: parent | null = null;
  showCommingledExperience: boolean = this.evService.showCommingledExperience;

  constructor() {
    const ngTourSteps = this.ngTourSteps;
    this.ngTourSteps.getContentfulDataFetchedValue().subscribe((data: boolean) => {
      if (data) this.ngTourUi.setTourSteps(ngTourSteps.dealDashboardSteps, {
        ...this.ngTourSteps.defaultOptions,
        isOptional: true
      });
    })
  }

  ngOnInit(): void {
    this.showNoAccessError = false;
    this.seoService.setCanonicalURL();
    const date = this.reportingdate();
    if (date) this.setReportingDate(date);
  }

  checkIfUserIsExternal() {
    if (this.userService.isExternalUser()) {
      this.checkExternalUserAccess();
    } else {
      this.getDealDashboardDetails();
    }
  }

  getMetaData(dealId?: number) {
    const loadingTask = 'getMetaData';
    this.loadingService.addAsyncTask(loadingTask);
    // getting reporting periods and breadcrumb data from new API endpoint
    this.apiService.getDealDashboardMetadata(this.seriesId() as number, dealId ?? this.dealId() as number).subscribe({
      next: (data: DealMeta) => {
        this.asOfDropdownData = [];
        data.reportingPeriods.forEach(date => {
          if (new Date(date).getTime() >= new Date(this.evService.hideDatesPriorToDate).getTime()) {
            this.asOfDropdownData.push(this.dateFormat.transform(date, "MM/dd/yyyy") as string)
          }
        })

        const selectedReportingPeriodExists = data.reportingPeriods.some(date =>
          this.dateFormat.transform(date, "MM/dd/yyyy") === this.selectedAsOfDate()
        );

        if (!selectedReportingPeriodExists) {
          this.isDashboardApiError = true;
        } else {
          this.isDealDataUnavailable = false;
          this.isDashboardApiError = false;
        }

        if (this.firstTimeLoadingDeals) {
          this.dealsForBreadcrumb.set(this.tableLogicService.sortDataForSeriesTable(data.relatedEntities));
          this.firstTimeLoadingDeals = false;
        }
        if (dealId && dealId !== Number(this.dealId())) {
          this.getDataForBreadcrumb(dealId)
        }
        this.getDealName();
        this.parentData = data.parent;
        this.updateBreadcrumbs(this.parentData);
        this.isDealDataUnavailable = this.isDashboardApiError;
        if (this.isDashboardApiError && !this.asOfDropdownData.includes(this.selectedAsOfDate())) {
          this.asOfDropdownData.push(this.selectedAsOfDate());
        }
        this.loadingService.resolveAsyncTask(loadingTask);
      },
      error: (err) => {
        this.loggingService.log("----- Deal Dashboard Metadata API Exception -----", err);
        this.loadingService.resolveAsyncTask(loadingTask);
      }
    });
  }

  getDealsCommonAssets() {
    this.contentfulService.fetchCommonAssets('deals', this.contentfulConfig.dealDashboard).subscribe((data) => {
      const typedData = data as LandingPageResponse;
      const labelItems = typedData?.data?.landingPage?.sectionsCollection?.items[0]?.resourcesCollection?.items;
      if (labelItems?.length > 0) {
        labelItems.forEach(item => {
          if (item.key == 'label.deal.detail') {
            this.dealDetailLabel = item.value;
          }
          if (item.key == 'label.asset.detail') {
            this.assetDetailLabel = item.value;
          }
        })
      }
    })
  }

  updateAsOfDate(date: string | string[] | Date | Date[] | (string | Date)[] | { start: string | Date; end: string | Date; type: string } | undefined) {
    if (!date || typeof date !== 'string') return;
    //assigns selectedAsOfDate (MM/dd/yyyy) and reportingPeriod (yyyy-MM-dd)
    this.setReportingDate(date);
    this.activityTracking.toggledAsOfDate(this.reportingPeriod())
    this.fetchDashboardDataFromApi(this.seriesId() as number, this.dealId() as number, this.reportingPeriod()).then((data: DealDashboard) => {
      this.assignDataToAllWidgets(data);
      if (this.isDealDataUnavailable) {
        this.getMetaData()
      }
      this.router.navigate(
        [], {
        relativeTo: this.activatedRoute,
        queryParams: { reportingdate: date },
        queryParamsHandling: 'merge'
      }).then(() => {
        this.updateBreadcrumbs(this.parentData);
      });
    });
  }

  goToSelectedDeal(dealId: string) {
    // check metadata first before going to selected deal in case selected as of date doesn't have data for deal
    this.getMetaData(Number(dealId))
  }

  getUrlFormattedDate(date: string) {
    return this.dateFormat.transform(date, 'yyyy-MM-dd');
  }

  getDataForBreadcrumb(dealId: number) {
    this.dealsForBreadcrumb().forEach(investmentType => {
      investmentType.subEntities.forEach(deal => {
        if (deal.displayId === dealId) {
          this.fetchDashboardDataFromApi(this.seriesId() as number, dealId, this.reportingPeriod()).then((data: DealDashboard) => {
            this.assignDataToAllWidgets(data);
            const route = `/investor/${this.investorId() as number}/series/${this.seriesId() as number}/deal/${dealId}`;
            this.dealNameForBreadcrumb.set(deal.displayName);
            this.managerIds = data.summary?.sponsors?.map(sponsor => sponsor.masterId) ?? [];
            this.getDealsCommonAssets();
            this.router.navigate([route], {
              queryParams: { reportingdate: this.reportingPeriod() },
              queryParamsHandling: 'merge'
            });
          });
          return;
        }
      })
    });
  }

  getDealName() {
    this.dealsForBreadcrumb().forEach(investmentType => {
      investmentType.subEntities.forEach(deal => {
        if (deal.displayId === Number(this.dealId())) {
          this.dealNameForBreadcrumb.set(deal.displayName);
        }
      })
    });
  }

  checkExternalUserAccess() {
    const assignedInvestorIds = this.userService.getInvestorIds();
    if (!assignedInvestorIds.includes(Number(this.investorId()))) {
      this.hasError = true;
      this.showNoAccessError = true;
      return;
    }
    else {
      this.hasError = false;
      this.getDealDashboardDetails();
    }
  }

  getDealDashboardDetails() {
    this.fetchDashboardDataFromApi(this.seriesId() as number, this.dealId() as number, this.reportingPeriod()).then((data: DealDashboard) => {
      this.assignDataToAllWidgets(data);
      //assigns selectedAsOfDate (MM/dd/yyyy) and reportingPeriod (yyyy-MM-dd)
      this.setReportingDate(data?.summary?.reportingPeriod?.slice(0, 10));
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          reportingdate: this.reportingPeriod()
        },
        queryParamsHandling: 'merge'
      });
      this.managerIds = data.summary?.sponsors?.map(sponsor => sponsor.masterId) ?? [];
      this.getMetaData();
    }).catch((err) => {
      this.loggingService.log("Error while fetching deal dashboard details", err);
    });
  }

  fetchDashboardDataFromApi(seriesId: number, dealId: number, reportingDate: string): Promise<DealDashboard> {
    const loadingTask = 'fetchDashboardDataFromApi';
    this.loadingService.addAsyncTask(loadingTask);
    return new Promise<DealDashboard>((resolve) => {
      this.apiService.getDealDashboardDetails(this.investorId() as number, seriesId, dealId, reportingDate).subscribe({
        next: (data) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          this.isDashboardApiError = false;
          resolve(data);
        },
        error: (err) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          this.hasError = true;
          this.errorMessage = this.errorMessageService.getErrorMessage(err);
          if (err && err?.error) {
            if (err?.error?.errors?.length > 0) {
              if (err?.error?.errors[0]?.message?.includes('User does not have access')) {
                this.isDashboardApiError = true;
                this.getMetaData();
              }
              if (err?.error?.errors[0]?.message?.includes('Cannot find data for deal')) {
                this.isDashboardApiError = true;
                // if we get this error, then we don't want to show regular has error message, so assign false to hasError
                this.hasError = false;
                this.getMetaData(dealId);
              }
            }
          }
          const route = `/investor/${this.investorId()}/series/${this.seriesId()}/deal/${dealId}`;
          this.router.navigate([route], {
            relativeTo: this.activatedRoute,
            queryParams: { reportingdate: reportingDate },
            queryParamsHandling: 'merge'
          });
          this.loggingService.log("Error while fetching deal data", err);
        }
      });
    })
  }

  assignDataToAllWidgets(data: DealDashboard) {
    this.dashboardData = data;
    if (data.assets) {
      this.assets = data.assets;
      this.assetSummary.update(current => ({ ...current, logo: '' }));
      this.isSingleAssetDeal = this.assets.length === 1;
      if (this.isSingleAssetDeal) {
        this.assetMasterId = this.assets[0].masterId;
        // We do not want to fetch the asset details for the masked asset
        if (this.assetMasterId != 0) {
          this.fetchCommonAssetsForSingleAssetDeal();
          const summary = this.assets[0].businessDescription && this.assets[0].businessDescription != '' ? this.assets[0].businessDescription : '';
          let showAssetUrl = false;
          let assetURL = '';
          if (this.assets[0].website && this.assets[0].website != '') {
            showAssetUrl = true;
            const url = this.assets[0].website;
            assetURL = !(/(http(s?)):\/\//i.test(url)) ? "https://" + url : url;
          }
          this.assetSummary.update(current => ({ ...current, summary, showAssetUrl, assetURL }));
        }
      }
    }
    if (data.widgets) {
      this.widgets = data.widgets
    }
    this.otherCountryNames = data.otherCountryNames ?? [];
    if (data.summary) {
      if (data.summary.assetClass === 'ARS') {
        data.summary.assetClass = 'Absolute Returns Strategy'
      }
      if (data.summary.assetClass) {
        this.assetClassImage = [data.summary.assetClass, this.iconSet()[this.assetClassPipe.transform(data.summary.assetClass) as string]];
      }
      this.summary = data.summary;
      this.currency = this.summary.currency
      this.dealTitle = this.summary.name;
      this.dealName.set(this.summary.dealName);
      this.seriesName.set(this.summary.portfolioSeriesName);
      this.portfolioName.set(this.summary.portfolioName);
      this.dealSummary = this.summary.dealDescription;
    }

    this.showCaseStudy = (this.summary.investmentSubType.toLowerCase() == ('co-investment') || this.summary.investmentSubType.toLowerCase() == ('direct investment')) && data.summary.assetClass == "Private Equity" && data.coInvestmentCaseStudy !== null;
    if (this.showCaseStudy) {
      this.coInvestmentCaseStudyDetails = data.coInvestmentCaseStudy;
    }
  }

  getSnapshot() {
    const dealId = this.dealId() as number;
    this.activityTracking.downloadSnapshot(dealId, this.dealTitle);
    const fileName = 'DealSnapshot_' + this.dealTitle.replace(/\s/g, "_") + '_' + this.reportingPeriod() + '.pdf'
    this.snapService.getSnapshot(this.reportingPeriod(), fileName, this.investorId() as number, this.seriesId() as number, dealId);
  }

  fetchCommonAssetsForSingleAssetDeal() {
    this.contentfulService.fetchCommonAssets('assetSummary', this.assets[0].masterId.toString()).subscribe((rawData) => {
      const assetData = rawData as AssetSummaryResponse;
      if (assetData.data.assetDetailsCollection.items.length === 0) {
        this.assetSummary.update(current => ({ ...current, logo: '', slidingImgs: [] }));
      }
      else {
        const assetDetails = assetData.data.assetDetailsCollection.items[0];
        const logo = assetDetails.logo ? assetDetails.logo.url : '';
        const slidingImgs = assetDetails.photosCollection.items.length !== 0
          ? assetDetails.photosCollection.items.map(photo => ({ src: photo.url }))
          : [];
        this.assetSummary.update(current => ({ ...current, logo, slidingImgs }));
      }
    });
  }

  breadcrumbClick(breadcrumb: Breadcrumb) {
    this.activityTracking.navigateToDashboard(breadcrumb.type === 'Dashboard' ? 'Investor' : breadcrumb.type, breadcrumb, 'deal dashboard breadcrumbs');
  }

  updateBreadcrumbs(parentData: parent | null) {
    this.breadcrumbs = this.breadcrumbDataService.getDashboardBreadcrumb(this.investorId() as number);
    //check if this deal is part of an aggregated portfolio series
    const isAggregatedSeries = parentData && parentData.disabled;
    let parentDataForBreadcrumbs = parentData;
    let seriesId = this.seriesId() as number;
    if (isAggregatedSeries) {
      parentDataForBreadcrumbs = parentData.parent;
      seriesId = parentData.parent.id;
    }
    const seriesBreadcrumbs = this.breadcrumbDataService.getPortfolioSeriesBreadcrumbs(this.investorId() as number, seriesId, parentDataForBreadcrumbs as unknown as BreadcrumbParentData);
    this.breadcrumbs.push(seriesBreadcrumbs);
    this.breadcrumbs.forEach(breadcrumb => {
      breadcrumb.queryParams = this.breadcrumbDataService.getQueryParamsForBreadcrumbs(breadcrumb);
    });
    if (isAggregatedSeries) {
      const underlyingSeriesBreadcrumbs = this.breadcrumbDataService.getUnderlyingPortfolioSeriesBreadcrumbs(parentData as unknown as BreadcrumbParentData);
      this.breadcrumbs.push(underlyingSeriesBreadcrumbs)
    };
  }

  getCaseStudy() {
    const assetName = this.coInvestmentCaseStudyDetails.summary.assetName;
    const assetId = this.coInvestmentCaseStudyDetails.summary.assetId;
    const fiscalDate = this.coInvestmentCaseStudyDetails.summary.calendarDate;
    const fileName = 'CaseStudy_' + assetName.replace(/\s/g, "_") + '_' + fiscalDate + '.pdf'
    this.snapService.getSnapshot(fiscalDate, fileName, this.investorId() as number, this.seriesId() as number, this.dealId() as number, assetId, true);
  }
}
